import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {map, switchMap} from 'rxjs';
import {BicToastService} from '@buildinginformationcloud/bic-ui-components';
import {RulesetService} from '../ruleset.service';
import {PermissionService} from '../permission/permission.service';

/**
 * Links in chatBIC-Antworten.
 *
 * <p>Die Werkzeuge des Backends liefern relative Links (`McpLinks`), die der Agent als Markdown
 * ausgibt – auf einen Bericht, einen Lauf oder zum Starten eines myChecks. Als gewöhnlicher
 * `<a href>` würde jeder Klick die Anwendung neu laden und dabei den Chat-Verlauf im Panel
 * verlieren. Interne Links gehen deshalb über den Router.
 *
 * <p>Der Startlink (`/projects/:id/my-checks?start=:rulesetId`) startet bewusst nicht im Backend:
 * Credit-Reservierung, Datei- und Regelauswahl und das Speichern des Ergebnisses leben in der
 * Ausführungsseite. Er geht deshalb denselben Weg wie der Startknopf der myCheck-Karte.
 */
@Injectable({providedIn: 'root'})
export class ChatLinkService {
  private router = inject(Router);
  private rulesetService = inject(RulesetService);
  private permissionService = inject(PermissionService);
  private toastService = inject(BicToastService);

  /** Läuft gerade ein Start? Verhindert doppelte Prüfläufe bei Doppelklick. */
  private starting = false;

  /**
   * Behandelt einen Klick in einer Chat-Antwort. Liefert `true`, wenn der Klick hier übernommen
   * wurde.
   *
   * <p>Externe Links öffnen in einem neuen Tab: im selben Tab verließ der Nutzer die Anwendung und
   * verlor das Chat-Panel samt laufender Antwort. Interne Pfade gehen über den Router – auch die
   * außerhalb von `/projects/`, die sonst die ganze Anwendung neu geladen hätten.
   */
  handleClick(event: MouseEvent): boolean {
    const anchor = (event.target as HTMLElement | null)?.closest?.('a');
    const href = anchor?.getAttribute('href');
    if (!href) return false;
    // Mittelklick, Strg/Cmd/Shift: neuer Tab bzw. Fenster bleibt Sache des Browsers.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) return false;

    let url: URL;
    try {
      url = new URL(href, window.location.origin);
    } catch {
      return false;
    }
    // mailto:, tel: usw. übernimmt der Browser selbst.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;

    event.preventDefault();
    if (url.origin !== window.location.origin) {
      window.open(url.href, '_blank', 'noopener,noreferrer');
      return true;
    }
    const start = /^\/projects\/(\d+)\/my-checks\/?$/.exec(url.pathname);
    const rulesetId = url.searchParams.get('start');
    if (start && rulesetId) {
      this.startMyCheck(Number(start[1]), rulesetId);
    } else {
      void this.router.navigateByUrl(url.pathname + url.search + url.hash);
    }
    return true;
  }

  /**
   * Externe Links einer gerenderten Antwort als solche kennzeichnen (`target=_blank`, `noopener`).
   * Der Klick läuft ohnehin über {@link handleClick}; das hier stimmt Mittelklick, Kontextmenü und
   * Statusleiste ab.
   */
  markExternalLinks(root: HTMLElement | null | undefined): void {
    if (!root) return;
    root.querySelectorAll<HTMLAnchorElement>('a[href]').forEach(anchor => {
      try {
        const url = new URL(anchor.getAttribute('href')!, window.location.origin);
        if ((url.protocol === 'http:' || url.protocol === 'https:') && url.origin !== window.location.origin) {
          anchor.target = '_blank';
          anchor.rel = 'noopener noreferrer';
        }
      } catch {
        // Unlesbares href: so lassen, wie es ist – der Browser zeigt es als normalen Link.
      }
    });
  }

  private startMyCheck(projectId: number, rulesetId: string): void {
    if (this.starting) return;
    if (!this.permissionService.hasProjectPrivilege('PROJECT_CHECK_EXECUTE')) {
      this.toastService.error('Ihnen fehlt die Berechtigung, Prüfungen auszuführen.');
      return;
    }
    this.starting = true;
    this.rulesetService.listRulesets(projectId).pipe(
      map(({prebuilt, custom}) => [...custom, ...prebuilt].find(ruleset => ruleset.id === rulesetId)),
      switchMap(ruleset => {
        if (!ruleset) {
          throw new Error(`myCheck ${rulesetId} ist in Projekt ${projectId} nicht verfügbar`);
        }
        return this.rulesetService.prepareDirectRun(ruleset, projectId);
      }),
    ).subscribe({
      next: definitionId => {
        this.starting = false;
        void this.router.navigate(['projects', projectId, 'check-runs', 'execute'], {
          queryParams: {definitionId},
        });
      },
      error: () => {
        this.starting = false;
        this.toastService.error('Die Prüfung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.');
      },
    });
  }
}
